/**
 * The app-wide selection recorder: one document-level listener set that
 * retains whatever the user selects, stamped with the focused tab.
 *
 * Additive to the two container-scoped selection hooks (the document viewer's
 * `useTextSelection`, the CM6 editor's) — they build popovers; this only
 * remembers text. Same discipline as the viewer's hook: a mouse selection is
 * published after mouseup settles (publishing mid-drag is harmless here, but
 * the final range is the one worth keeping), a collapse is not a dismissal,
 * and only the primary button arms the drag latch (a right-click's mouseup is
 * often swallowed by the native menu).
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

import { tabKey } from './focusedTab';
import { useSelectionStore } from './selectionStore';
import { useFocusedTab } from './useActiveContext';

/** Grace period after mouseup for the browser to finalise its selection. */
export const SELECTION_SETTLE_MS = 10;

/** `MouseEvent.button` for the left/primary button — the only one that selects. */
const PRIMARY_BUTTON = 0;

/**
 * True when the DOM selection lives inside a text control (the Go box, the
 * chat composer). Engines expose those selections inconsistently — Blink's
 * `toString()` returns the control's text while `isCollapsed` says true — so
 * the exclusion is explicit rather than left to a quirk.
 */
function selectionIsInTextControl(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

/**
 * Record the user's selections app-wide, stamped with the focused tab's key.
 * Mount once, in the authenticated layout; the context object reads the
 * result through `useActiveContext`.
 */
export function useSelectionRecorder(): void {
  const key = tabKey(useFocusedTab());
  const keyRef = useRef(key);
  // Layout effect, not passive: a `selectionchange` queued behind a deferred
  // passive flush would otherwise be stamped with the previous tab's key.
  useLayoutEffect(() => {
    keyRef.current = key;
  }, [key]);

  useEffect(() => {
    let dragging = false;
    // Whether a `selectionchange` arrived during the current drag. A press and
    // release that changed nothing (a button click in the other pane) must not
    // re-stamp the retained selection with whatever tab the click focused.
    let changedDuringDrag = false;
    let settle: ReturnType<typeof setTimeout> | undefined;

    const capture = (): void => {
      if (dragging) {
        changedDuringDrag = true;
        return;
      }
      if (selectionIsInTextControl()) return;
      const sel = window.getSelection();
      // No range to read: leave the retained one alone.
      if (!sel || sel.isCollapsed) return;
      useSelectionStore.getState().record(sel.toString(), keyRef.current);
    };
    const onMouseDown = (e: MouseEvent): void => {
      if (e.button !== PRIMARY_BUTTON) return;
      dragging = true;
      changedDuringDrag = false;
    };
    const endDrag = (): void => {
      dragging = false;
    };
    const onMouseUp = (): void => {
      endDrag();
      if (!changedDuringDrag) return;
      changedDuringDrag = false;
      // Let the browser finalise the selection before reading it.
      settle = setTimeout(capture, SELECTION_SETTLE_MS);
    };

    // Capture phase: a component that stops `mouseup` from bubbling must not
    // leave the drag latch stuck (which would mute keyboard selections).
    const opts = { capture: true } as const;
    document.addEventListener('selectionchange', capture);
    document.addEventListener('mousedown', onMouseDown, opts);
    document.addEventListener('mouseup', onMouseUp, opts);
    document.addEventListener('dragend', endDrag, opts);
    window.addEventListener('blur', endDrag);
    return () => {
      clearTimeout(settle);
      document.removeEventListener('selectionchange', capture);
      document.removeEventListener('mousedown', onMouseDown, opts);
      document.removeEventListener('mouseup', onMouseUp, opts);
      document.removeEventListener('dragend', endDrag, opts);
      window.removeEventListener('blur', endDrag);
    };
  }, []);
}
